import React, { useState } from 'react';
import { PlayButton } from '../home/PlayButton';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { AuthScreen } from '../auth/AuthScreen';
import { FriendRoomScreen } from './FriendRoomScreen';
import { SaveProgressPrompt } from '../ui/SaveProgressPrompt';
import { GameSettings } from '../settings/SettingsScreen';
import { EyeOff, Eye } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
interface WelcomeScreenProps {
  onPlay: (hideProduct: boolean) => void;
  onTutorial: () => void;
  onFriendGame: (roomCode: string, settings: GameSettings & {
    hideProduct: boolean;
  }) => void;
}
export function WelcomeScreen({
  onPlay,
  onTutorial,
  onFriendGame
}: WelcomeScreenProps) {
  const {
    user,
    anonymousProgress,
    showSaveProgressPrompt,
    setShowSaveProgressPrompt,
    signIn
  } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [showFriendRoom, setShowFriendRoom] = useState(false);
  const [hideProduct, setHideProduct] = useState(false);
  const handleAuthSuccess = (userData: {
    email: string;
    name: string;
  }) => {
    signIn(userData.email, userData.name);
    setShowAuth(false);
  };
  const handleSignUpFromPrompt = () => {
    setShowSaveProgressPrompt(false);
    setShowAuth(true);
  };
  return <div className="flex flex-col h-full p-6 bg-slate-50" data-id="element-596">
      <div className="flex-1 flex flex-col items-center justify-center gap-8" data-id="element-597">
        <div className="text-center space-y-2" data-id="element-598">
          <div className="inline-block p-4 bg-white rounded-3xl shadow-xl mb-4" data-id="element-599">
            <img src="https://cdn.magicpatterns.com/uploads/cSWDoJ881q2Jhi42ScyEen/Connectiply.png" alt="Connectiply" className="w-16 h-16 object-contain" data-id="element-600" />
          </div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight" data-id="element-601">
            Connectiply
          </h1>
          <p className="text-xl text-slate-500 font-medium" data-id="element-602">
            Math meets strategy.
          </p>
        </div>

        <div className="w-full max-w-sm space-y-4" data-id="element-603">
          <div className="bg-white p-6 rounded-3xl shadow-lg border border-slate-100 text-center space-y-4" data-id="element-604">
            <h2 className="text-2xl font-bold text-slate-800" data-id="element-605">
              Play in 10 seconds
            </h2>
            <PlayButton onClick={() => onPlay(hideProduct)} data-id="element-606" />
            <p className="text-xs text-slate-400 font-medium uppercase tracking-wide" data-id="element-607">
              Quick Play vs AI
            </p>

            <div className="pt-3 border-t border-slate-200" data-id="element-608">
              <button onClick={() => setHideProduct(!hideProduct)} className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-colors" data-id="element-609">
                <div className="flex items-center gap-2 text-left" data-id="element-610">
                  {hideProduct ? <EyeOff className="text-slate-600" size={18} data-id="element-611" /> : <Eye className="text-slate-600" size={18} data-id="element-612" />}
                  <div data-id="element-613">
                    <div className="text-sm font-semibold text-slate-900" data-id="element-614">
                      {hideProduct ? 'Product Hidden' : 'Show Product'}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-615">
                      {hideProduct ? 'Challenge mode' : 'Easier mode'}
                    </div>
                  </div>
                </div>
                <div className={`w-12 h-7 rounded-full transition-colors relative ${hideProduct ? 'bg-amber-500' : 'bg-slate-300'}`} data-id="element-616">
                  <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${hideProduct ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-617" />
                </div>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-618">
            <Button variant="secondary" className="h-14 rounded-2xl" onClick={onTutorial} data-id="element-619">
              🎓 Tutorial
            </Button>
            <Button variant="outline" className="h-14 rounded-2xl" onClick={() => setShowFriendRoom(true)} data-id="element-620">
              👥 Friend
            </Button>
          </div>
        </div>
      </div>

      <div className="text-center mt-5" data-id="element-621">
        {user ? <div className="text-sm text-slate-600" data-id="element-622">
            Welcome back, <strong data-id="element-623">{user.name}</strong>!
          </div> : <button onClick={() => setShowAuth(true)} className="text-sm text-slate-400 font-semibold hover:text-slate-600" data-id="element-624">
            Sign in for streaks & rewards →
          </button>}
      </div>

      <AuthScreen isOpen={showAuth} onClose={() => setShowAuth(false)} onAuthSuccess={handleAuthSuccess} data-id="element-625" />

      <FriendRoomScreen isOpen={showFriendRoom} onClose={() => setShowFriendRoom(false)} onStartGame={onFriendGame} data-id="element-626" />

      <SaveProgressPrompt isOpen={showSaveProgressPrompt} onClose={() => setShowSaveProgressPrompt(false)} onSignUp={handleSignUpFromPrompt} stats={{
      games: anonymousProgress.games,
      wins: anonymousProgress.wins,
      xp: anonymousProgress.xp
    }} data-id="element-627" />
    </div>;
}